import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, FormControl,
  InputLabel, Select, MenuItem, CircularProgress, Alert, Paper, Chip
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon, Send as SendIcon,
  CheckCircle as CheckIcon, Cancel as CancelIcon,
  HourglassEmpty as PendingIcon
} from '@mui/icons-material';
import { studentAPI } from '../../services/api';

// ─── Status display card ──────────────────────────────────────────────────────
const RequestStatusCard = ({ request, onNewRequest }) => {
  const statusMap = {
    pending:  { label: 'قيد المراجعة',  color: 'warning', icon: <PendingIcon />, bg: '#fff8e1' },
    approved: { label: 'تمت الموافقة',  color: 'success', icon: <CheckIcon />,   bg: '#e8f5e9' },
    rejected: { label: 'مرفوض',         color: 'error',   icon: <CancelIcon />,  bg: '#ffebee' },
  };
  const s = statusMap[request.status] || statusMap.pending;

  return (
    <Card sx={{ border: `2px solid`, borderColor: `${s.color}.main`, bgcolor: s.bg }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          {s.icon}
          <Typography variant="h6">حالة طلب البطاقة</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="body1">الحالة:</Typography>
          <Chip label={s.label} color={s.color} />
        </Box>
        <Typography variant="body2" color="text.secondary">
          تاريخ الطلب: {new Date(request.created_at).toLocaleDateString('ar-MA')}
        </Typography>
        {request.updated_at && request.status !== 'pending' && (
          <Typography variant="body2" color="text.secondary">
            تاريخ المعالجة: {new Date(request.updated_at).toLocaleDateString('ar-MA')}
          </Typography>
        )}
        {request.comment && (
          <Alert severity={request.status === 'approved' ? 'success' : 'error'} sx={{ mt: 2 }}>
            <Typography variant="body2"><strong>ملاحظة الإدارة:</strong> {request.comment}</Typography>
          </Alert>
        )}
        {request.status === 'rejected' && (
          <Button variant="outlined" color="primary" sx={{ mt: 2 }} onClick={onNewRequest}>
            إعادة تقديم الطلب
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Submit form ──────────────────────────────────────────────────────────────
const RequestStudentCard = () => {
  const [proofOfLoss, setProofOfLoss] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false); // hard guard against double-submit
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [latestRequest, setLatestRequest] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await studentAPI.getStudentCardRequestStatus();
        setLatestRequest(response.latest_request || null);
        // Show form only if there's no previous request at all
        if (!response.latest_request) {
          setShowForm(true);
        }
      } catch (err) {
        console.error('Could not check request status:', err);
        setShowForm(true);
      } finally {
        setIsLoading(false);
      }
    };
    checkStatus();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!proofOfLoss) { setSubmitError('الرجاء إرفاق إثبات الضياع.'); return; }
    if (submittingRef.current) return; // block any double-fire
    submittingRef.current = true;
    setIsSubmitting(true);
    setSubmitError(null);
    const formData = new FormData();
    formData.append('proofOfLoss', proofOfLoss);
    try {
      await studentAPI.requestStudentCard(formData);
      setSubmitSuccess(true);
      setShowForm(false);
      setLatestRequest({ status: 'pending', created_at: new Date().toISOString(), comment: '' });
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'حدث خطأ أثناء إرسال الطلب.');
      submittingRef.current = false; // allow retry on error
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
  }

  // Show status if there's a pending or approved request
  if (latestRequest && !showForm) {
    return <RequestStatusCard request={latestRequest} onNewRequest={() => setShowForm(true)} />;
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>طلب بطاقة الطالب</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          في حالة ضياع بطاقتك، يرجى إرفاق نسخة من إثبات الضياع (صورة أو PDF).
        </Typography>

        {submitSuccess ? (
          <Alert severity="success">تم إرسال طلبك بنجاح. سيتم إعلامك عند معالجته.</Alert>
        ) : (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Button variant="contained" component="label" startIcon={<CloudUploadIcon />} fullWidth>
                  تحميل إثبات الضياع
                  <input type="file" hidden onChange={(e) => setProofOfLoss(e.target.files[0])} accept="image/*,application/pdf" />
                </Button>
                {proofOfLoss && (
                  <Box sx={{ mt: 2 }}>
                    <Chip label={proofOfLoss.name} onDelete={() => setProofOfLoss(null)} />
                  </Box>
                )}
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit" variant="contained" color="primary" fullWidth
                  disabled={isSubmitting || !proofOfLoss}
                  startIcon={isSubmitting ? <CircularProgress size={20} /> : <SendIcon />}
                >
                  {isSubmitting ? 'جاري الإرسال...' : 'إرسال الطلب'}
                </Button>
              </Grid>
            </Grid>
            {submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}
          </form>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────
const StudentRequests = () => {
  const [requestType, setRequestType] = useState('student_card');

  const renderRequestForm = () => {
    switch (requestType) {
      case 'student_card': return <RequestStudentCard />;
      case 'diploma':
        return <Paper sx={{ p: 4, textAlign: 'center' }}><Typography variant="h6">طلب شهادة التخرج</Typography><Typography>هذه الخدمة ستكون متاحة قريبا.</Typography></Paper>;
      case 'official_documents':
        return <Paper sx={{ p: 4, textAlign: 'center' }}><Typography variant="h6">طلب وثائق إدارية</Typography><Typography>هذه الخدمة ستكون متاحة قريبا.</Typography></Paper>;
      case 'reclamations':
        return <Paper sx={{ p: 4, textAlign: 'center' }}><Typography variant="h6">تقديم شكاية</Typography><Typography>هذه الخدمة ستكون متاحة قريبا.</Typography></Paper>;
      default: return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="600" color="primary" sx={{ mb: 3 }}>
        📄 الطلبات والشكايات
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>نوع الطلب</InputLabel>
            <Select value={requestType} label="نوع الطلب" onChange={(e) => setRequestType(e.target.value)}>
              <MenuItem value="student_card">طلب بطاقة الطالب</MenuItem>
              <MenuItem value="diploma">طلب شهادة التخرج</MenuItem>
              <MenuItem value="official_documents">طلب وثائق إدارية</MenuItem>
              <MenuItem value="reclamations">شكاية</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={8}>
          {renderRequestForm()}
        </Grid>
      </Grid>
    </Box>
  );
};

export default StudentRequests;
